/**
 * Resolving the active cart as a PAIR: an order id and the guest token that
 * proves ownership of that exact order.
 *
 * The call sites used to rebuild the pair ad hoc, each with its own fallback
 * chain, and — critically — the two halves were resolved INDEPENDENTLY:
 *
 *   // handleUpload
 *   let orderId   = order?.id || mainOrderId;             // React Query cache, then state
 *   let orderToken = guestToken || urlGuestToken;         // state, then URL
 *
 * Nothing there ties the two together. `order?.id` is served from the React
 * Query cache, which outlives the state that produced it (the query is disabled
 * when the token goes missing, but the cached data stays), so the id resolves
 * while the token comes back empty. The request then goes out with no token at
 * all and the backend's OrderOwnerGuard answers exactly what it sees:
 *
 *   401 "No authentication token provided"      (FOTO-NEXTJS-147, 43 events)
 *
 * The sibling `handleContinueToCheckout` had a different chain again — it did
 * consult the cookie for the token, which `handleUpload` never did — so the two
 * flows could disagree about which order the customer is even on. Feeding an id
 * from one session and a token from another produces the other half of the
 * guard's vocabulary, "You do not have permission to access this order".
 *
 * The rule this module enforces: a source is used only when it can supply BOTH
 * halves, and the halves are never mixed across sources. A source holding half
 * a session is skipped, not scavenged.
 */

import { parseCookies } from "nookies";
import { getOrderData } from "./local-storage.util";
import { guestTokenFromUrl } from "./guest-token-url-guard";

/** Where a resolved pair came from. Reported to Sentry as `tokenSource`. */
export type OrderSessionSource = "context" | "url" | "cookie" | "storage";

export interface OrderSession {
  orderId: string;
  guestToken: string;
  source: OrderSessionSource;
}

/** One candidate store. Either half may be absent, empty, or null. */
export interface OrderSessionCandidate {
  orderId?: string | null;
  guestToken?: string | null;
}

/**
 * The stores, in the order they are trusted.
 *
 * `context` first because React state is the freshest thing we have — it is
 * written the moment an order is created. The URL comes next (a checkout
 * redirect carries the pair explicitly), then the shared cookie, and finally
 * the localStorage backup, which is the longest-lived and therefore the most
 * likely to be stale.
 */
export interface OrderSessionStores {
  context?: OrderSessionCandidate;
  url?: OrderSessionCandidate;
  cookie?: OrderSessionCandidate;
  storage?: OrderSessionCandidate;
}

const SOURCE_PRIORITY: readonly OrderSessionSource[] = [
  "context",
  "url",
  "cookie",
  "storage",
];

/** Trims and rejects the empty string, which every store uses to mean "unset". */
export function clean(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  // "false" is written into the guest-token header slot when the session is
  // cleared, and has leaked into cookies from there. It is not a token.
  if (!trimmed || trimmed === "false" || trimmed === "undefined") return null;
  return trimmed;
}

/**
 * First store that holds a COMPLETE pair, or null when none does.
 *
 * Returning null is a real answer: it means there is no session to resume, and
 * the caller should create a fresh order rather than fire a request that is
 * guaranteed to 401.
 */
export function resolveOrderSession(
  stores: OrderSessionStores,
): OrderSession | null {
  for (const source of SOURCE_PRIORITY) {
    const candidate = stores[source];
    if (!candidate) continue;
    const orderId = clean(candidate.orderId);
    const guestToken = clean(candidate.guestToken);
    // Half a session is not a session. Taking the id here and letting the token
    // fall through to the next store is precisely the bug this replaces.
    if (orderId && guestToken) return { orderId, guestToken, source };
  }
  return null;
}

/**
 * The most trusted order id we know of, with or without a token.
 *
 * Only safe for callers that can authenticate some other way. A SIGNED-IN
 * customer's order is authorised by their JWT and never has a guest token at
 * all, so for them a bare id is a whole session; a guest with a bare id has no
 * way to prove ownership and any request they send is a guaranteed 401.
 *
 * Never use this to fill in the id half of a pair whose token came from
 * somewhere else -- that is the mismatch this module exists to prevent.
 */
export function resolveOrderId(stores: OrderSessionStores): string | null {
  for (const source of SOURCE_PRIORITY) {
    const orderId = clean(stores[source]?.orderId);
    if (orderId) return orderId;
  }
  return null;
}

/**
 * Read every browser-side store of the pair, ready for resolveOrderSession.
 *
 * The point of having ONE reader: each call site used to assemble its own
 * subset, and the subsets disagreed. OrderProvider's restore read the URL and
 * the cookie only — no localStorage — so when a mobile browser evicted the
 * cookie (routine on Safari and in-app webviews) the provider restored nothing,
 * `mainOrderId` stayed null, the cart rendered empty, and the next add-to-cart
 * created a DUPLICATE order while a perfectly good session sat unread in
 * localStorage. Meanwhile order/new read URL then localStorage but never the
 * cookie. Same customer, same page load, two different answers.
 *
 * Reading a store is always safe: nothing here decides that a cart is dead.
 * Whether a restored session may still act as the cart is decided once, on
 * landing, from the server's own answer (order-freshness.ts).
 *
 * @param context the in-memory pair (React state), the freshest source there is
 */
export function readOrderSessionStores(
  context?: OrderSessionCandidate,
): OrderSessionStores {
  return {
    context,
    url: readUrlCandidate(),
    cookie: readCookieCandidate(),
    storage: readStorageCandidate(),
  };
}

function readUrlCandidate(): OrderSessionCandidate {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("orderId");
  return {
    orderId,
    // Either spelling (`guestToken` is the LEGACY one, still written by older
    // links), or the token the head guard took out of this URL so analytics
    // could not read it — see guest-token-url-guard.ts. Missing either would
    // silently halve a session that is fully present.
    guestToken: guestTokenFromUrl(orderId),
  };
}

function readCookieCandidate(): OrderSessionCandidate {
  try {
    const cookies = parseCookies();
    return { orderId: cookies.orderId, guestToken: cookies.guest_token };
  } catch {
    // A store we cannot read is a store with nothing in it — never an error
    // that should stop the other three from being consulted.
    return {};
  }
}

function readStorageCandidate(): OrderSessionCandidate {
  // getOrderData already swallows its own exceptions (private-mode Safari
  // throws on localStorage access) and returns null.
  const saved = getOrderData();
  return { orderId: saved?.orderId, guestToken: saved?.guestToken };
}

/**
 * True when some store held an order id but no usable pair could be built.
 *
 * This is the "half-lost session" signature the order-loss diagnostics report
 * on: the customer demonstrably had an order, and we are about to start a new
 * one on top of it.
 */
export function hasOrphanedOrderId(stores: OrderSessionStores): boolean {
  if (resolveOrderSession(stores)) return false;
  return resolveOrderId(stores) !== null;
}

/** What one store was holding, in the resolver's own terms. */
export type StoreContents = "both" | "id-only" | "token-only" | "none";

/**
 * A picture of all four stores at one instant, small enough to be tags.
 *
 * `resolvedFrom` is the store the resolver actually took the pair from, or
 * "none" when no store held a complete one.
 */
export interface SessionStoreSummary {
  context: StoreContents;
  url: StoreContents;
  cookie: StoreContents;
  storage: StoreContents;
  resolvedFrom: OrderSessionSource | "none";
  /** The signature of a half-lost session: an id survives, no usable pair does. */
  orphanedOrderId: boolean;
}

function describeStore(candidate: OrderSessionCandidate | undefined): StoreContents {
  const orderId = clean(candidate?.orderId);
  const guestToken = clean(candidate?.guestToken);
  if (orderId && guestToken) return "both";
  if (orderId) return "id-only";
  if (guestToken) return "token-only";
  return "none";
}

/**
 * Which stores held what, for the order-loss report.
 *
 * The creation report used to carry two booleans read straight off the cookie
 * (`cookieHadOrderId` / `cookieHadGuestToken`) and nothing else, so the two
 * failures that actually strand customers were invisible in it:
 *
 *   - a BLIND SPOT: a complete pair sitting in localStorage that the reader
 *     never consulted. That is 66d8ede2 and bca7d728 — the provider read the URL
 *     and the cookie only, so a mobile browser evicting the cookie was enough to
 *     make it create a second order on top of a live one. From the old report
 *     that is indistinguishable from a first-time visitor;
 *   - a HALF SESSION spread across stores: an id in the URL, a token in the
 *     cookie. Neither store is complete, the resolver correctly refuses to mix
 *     them, and the customer's cart is unreachable.
 *
 * Both are obvious at a glance from four per-store tags and are guesswork
 * without them.
 *
 * Deliberately uses the same `clean()` the resolver uses, so this describes what
 * the RESOLVER saw and not what is literally in the store: a cookie holding the
 * string "false" (which is what the header slot writes on clear, and it has
 * leaked into cookies from there) reads as absent in both places. A summary that
 * disagreed with the decision it is explaining would be worse than none.
 *
 * Contains no token, no id and no URL — only which halves were present.
 */
export function summariseSessionStores(
  stores: OrderSessionStores,
): SessionStoreSummary {
  return {
    context: describeStore(stores.context),
    url: describeStore(stores.url),
    cookie: describeStore(stores.cookie),
    storage: describeStore(stores.storage),
    resolvedFrom: resolveOrderSession(stores)?.source ?? "none",
    orphanedOrderId: hasOrphanedOrderId(stores),
  };
}
